import { useMemo } from 'react';
import { useWorkouts } from './useWorkouts';
import { useHabits } from './useHabits';
import { useWater } from './useWater';
import { useAchievements } from './useAchievements';

/**
 * XP/level/coins are pure derived values from existing history (same pattern
 * as achievements) rather than an incremental ledger, so they can never drift
 * out of sync or double-count. Deterministic formula, not AI-driven.
 */
export function useGamification() {
  const { workouts } = useWorkouts();
  const { weekData: habitWeek } = useHabits();
  const { weekData: waterWeek, goalMl } = useWater();
  const { unlocked } = useAchievements();

  return useMemo(() => {
    const workoutXp = workouts.length * 10;
    const habitCompletions = habitWeek.reduce(
      (sum, d) => sum + Object.values(d.log).filter(Boolean).length,
      0
    );
    const habitXp = habitCompletions * 5;
    const waterGoalDays = waterWeek.filter((d) => d.amountMl >= goalMl).length;
    const waterXp = waterGoalDays * 2;

    const xp = workoutXp + habitXp + waterXp;
    const level = Math.floor(Math.sqrt(xp / 50));
    const coins = unlocked.size * 10;
    const xpForCurrentLevel = 50 * level * level;
    const xpForNextLevel = 50 * (level + 1) * (level + 1);
    const levelProgress = xpForNextLevel > xpForCurrentLevel
      ? (xp - xpForCurrentLevel) / (xpForNextLevel - xpForCurrentLevel)
      : 0;

    return { xp, level, coins, levelProgress, xpForNextLevel };
  }, [workouts, habitWeek, waterWeek, goalMl, unlocked]);
}
